<template>
  <div v-if="userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.LIST)">
    <parcel-update
      v-if="viewMode === 'update_parcels'"
      :parcel-list="selectedParcels"
      @parcel_Update_Cancelled="parcelUpdateCancelled"
      @parcel_Update_Saved="parcelUpdateSaved" />
    <div v-show="viewMode !== 'update_parcels'">
      <!-- *********************** Search Header *********************************** -->
      <main-header
        @onSearch="searchParcels"
        @onSearchCancel="loadParcelList" />

      <!-- *********************** Filter Header *********************************** -->
      <parcel-filter
        v-show="viewMode === 'parcelList' || viewMode === 'guard'"
        filter-name="parcel"
        @onReload="loadParcelList" />

      <!-- *********************** Parcel list *********************************** -->
      <div v-show="viewMode === 'parcelList'" class="parcel-list-header padding-wrapper">
        <div v-if="$mq !== 'sm'" class="row">
          <div class="col9 sm-col12">
            <!-- *********************** Export to excel *********************************** -->
            <form-button class="parcel-list-header__excel" @onClick="downloadExcel">
              Export Excel
            </form-button>
            <form-button @onClick="copyHTML">
              Copy
            </form-button>
            <form-input
              v-model="filterTerm"
              placeholder="Filter List"
              class="parcel-list-header__filter"
              @input="onFilterChange" />
            <!-- *********************** Parcel count *********************************** -->
            <span>{{ totalRowCount }} Parcels</span>
          </div>

          <!-- *********************** Parcel List controls *********************************** -->
          <div class="col3 sm-col12">
            <list-controls
              :column-groups="listHeaderColumnGroups"
              :parcel-list-expanded="allColumnsShown"
              @expandList="expandList"
              @contractList="contractList"
              @toggleColumn="toggleColumn" />
          </div>
        </div>
      </div>

      <!-- *********************** Parcel List Table *********************************** -->
      <parcel-list-table
        v-show="!loading_parcels && viewMode === 'parcelList'"
        ref="parcelListTable"
        :parcel-list="parcelList"
        :num-selected-parcels="selectedParcels.length"
        :list-header-column-groups="getColumnGroup()"
        @selectionChanged="onSelectionChanged"
        @parcelDetailOpened="onParcelDetailOpened" />

      <div v-if="viewMode === 'guard'" class="parcel-list__guard">
        <p>You have no active filter besides date, loading it anyway could take quite a long time</p>
        <form-button @onClick="handleLoadAnyway">
          Load anyway
        </form-button>
      </div>

      <!-- *********************** Parcels Detail modal *********************************** -->
      <parcel-detail-content
        v-if="viewMode === 'parcelDetails'"
        :parcel-key="parcelID"
        @detailClosed="onDetailClosed"
        @reloadParcelList="reloadParcelList" />

      <!-- *********************** Action bar for selected parcels *********************************** -->
      <parcel-action-bar
        v-show="showActionBar"
        @openDeleteParcelModal="onOpenDeleteParcelModal"
        @openReactivateParcelModal="onOpenReactivateParcelModal"
        @openUpdateParcelsModal="openUpdateParcelsModal"
        @openUploadDocumentModal="onOpenUploadDocumentModal"
        @deactivateProblemReport="onDeactivateProblemReport"
        @openCreateCMRModal="onOpenCreateCMRModal"
        @deactivateEvent="onDeactivateEvent"
        @openCreateAdhocDriveModal="openCreateAdhocDriveModal"
        @openCreateLinehaulDriveModal="openCreateLinehaulDriveModal" />
      <!-- *********************** Summary of selected parcels  *********************************** -->
      <parcel-list-table-summary
        v-show="selectedParcels.length > 1 && viewMode === 'parcelList'"
        :rows-selected="selectedParcels.length"
        :summary-items="summaryItems" />

      <!-- *********************** Report Problem modal pane  *********************************** -->
      <create-problem-report-modal
        v-if="userHasPermission(constants.PERMISSION_TYPES.PROBLEM_REPORT, constants.PERMISSION_VALUES.CREATE) && viewMode === 'parcelList'"
        :selected-rows="selectedParcels"
        @problemReportCreated="reloadParcelList" />

      <scan-parcels-modal
        v-if="userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.SCAN) && viewMode === 'parcelList'"
        :selected-rows="selectedParcels"
        @parcelScanned="onClearAndReloadParcelList"
        @event_updated="onClearAndReloadParcelList" />

      <file-upload-modal
        v-if="userHasPermission(constants.PERMISSION_TYPES.DOCUMENT, constants.PERMISSION_VALUES.UPLOAD) && viewMode === 'parcelList'"
        @fileUploaded="onFileUploaded" />

      <deactivate-event-modal
        v-if="userHasPermission(constants.PERMISSION_TYPES.EVENT, constants.PERMISSION_VALUES.DELETE) && viewMode === 'parcelList'"
        @eventsDeactivated="onEventsDeactivated" />

      <delete-parcel-modal
        v-if="userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.DELETE) && viewMode === 'parcelList'"
        :parcels="parcelsToDelete"
        @parcelDeleted="onClearAndReloadParcelList" />

      <reactivate-parcel-modal
        v-if="userHasPermission(constants.PERMISSION_TYPES.PARCEL, constants.PERMISSION_VALUES.REACTIVATE) && viewMode === 'parcelList'"
        :parcels="parcelsToReactivate"
        @parcelReactivated="onClearAndReloadParcelList" />

      <create-cmr-modal
        v-if="userHasPermission(constants.PERMISSION_TYPES.CMR, constants.PERMISSION_VALUES.CREATE) && viewMode === 'parcelList'"
        :parcels="parcelsToCreateCMR"
        @cmrCreated="onCMRCreated" />

    </div>
    <!-- *********************** Multiple Update Parcel *********************************** -->
    <!-- *********************** Loading Gif *********************************** -->
    <div v-show="loading_parcels" class="spl-load-img" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Helpers from '@/helpers.vue'
import webApi from '@/webApi'
import constants from '@/constants'
import { map, flatMap, filter, sumBy, size, find, findIndex, isEmpty, groupBy } from 'lodash'
import { mapGetters } from 'vuex'
import Fuse from 'fuse.js'

export default Vue.extend({
  name: 'Parcellist',
  mixins: [Helpers],
  data () {
    const listHeaderColumnGroupsOrig = [
      {
        label: 'ID',
        columns: [
          { name: constants.REFERENCE_TYPE_NAME.COUNTER_NUMBER, label: 'Counter Number', selected: true, long: true, fixed: true, type: 'String' }
        ]
      },
      {
        label: 'General Info',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.TRACKER_LINK, label: 'Tracker Link', selected: true, type: 'String', fixedSecond: true, fixed: false },
          { name: constants.REFERENCE_TYPE_NAME.FORWARDER_REFERENCE, label: 'Forwarder Ref.', selected: true, long: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.AWB, label: 'AWB', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.HAWB, label: 'HAWB', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.CUSTOMER_NAME, label: 'Company', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.COUNTRY_NAME, label: 'Country', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.CURRENT_PARCEL_STATUS, label: 'Parcel Status', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.CONTACT_PHONE_NUMBER, label: 'Contact Phone Number', selected: true, type: 'String' }
        ]
      },
      {
        label: 'Current Status',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.LATEST_NOTE, label: 'Latest Note', selected: true, long: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.PROBLEM_TYPE_DESCRIPTION, label: 'Problem', selected: true, bold: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.CURRENT_STATUS_EVENT_NAME, label: 'Current Event', selected: true, bold: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.CURRENT_STATUS_EVENT_TIMESTAMP, label: 'Timestamp', selected: true, type: 'Time' },
          { name: constants.PARCEL_FIELD_KEYS.CURRENT_STATUS_EVENT_USER_NAME, label: 'Scanned By', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.CLOSE_DEADLINE, label: 'Deadline', selected: true, type: 'Time' }
        ]
      },
      {
        label: 'Service Info',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.XRAYED, label: 'X Rayed', selected: true, type: 'Boolean' },
          { name: constants.PARCEL_FIELD_KEYS.Last_Mile_Upgrade, label: 'Last Mile Upgrade', selected: true, type: 'Boolean' },
          { name: constants.PARCEL_FIELD_KEYS.SERVICE_LEVEL_NAME, label: 'Service Level', selected: true, type: 'String' }
        ]
      },
      {
        label: 'Dangerous Goods',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.DANGEROUS_GOODS, label: 'DGR', selected: true, type: 'Boolean' },
          { name: constants.PARCEL_FIELD_KEYS.DANGEROUS_GOODS_UN_CODE, label: 'UN Code', selected: false, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DANGEROUS_GOODS_CLASS_OR_DIVISION, label: 'Class', selected: false, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DANGEROUS_GOODS_PACKING_GROUP, label: 'PG', selected: false, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DANGEROUS_GOODS_PACKING_INSTRUCTION, label: 'PI', selected: false, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DANGEROUS_GOODS_PROPER_SHIPPING_NAME, label: 'Proper Shipping Name', selected: false, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DANGEROUS_GOODS_WEIGHT, label: 'DGR Wt (kg)', selected: false, type: 'Number' },
          { name: constants.PARCEL_FIELD_KEYS.DANGEROUS_GOODS_VOLUME, label: 'DGR Vol (m3)', selected: false, type: 'Number' }
        ]
      },
      {
        label: 'Delivery Address',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.DELIVERY_NAME_1, label: 'Delivery Name', selected: true, long: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DELIVERY_STREET_1, label: 'Delivery Street', selected: true, long: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DELIVERY_POSTAL_CODE, label: 'Delivery Postal Code', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DELIVERY_CITY, label: 'Delivery City', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.DELIVERY_COUNTRY_NAME, label: 'Delivery Country', selected: true, type: 'String' }
        ]
      },
      {
        label: 'Pickup Address',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.PICKUP_NAME_1, label: 'Pickup Name', selected: true, long: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.PICKUP_STREET_1, label: 'Pickup Street', selected: true, long: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.PICKUP_POSTAL_CODE, label: 'Pickup Postal Code', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.PICKUP_CITY, label: 'Pickup City', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.PICKUP_COUNTRY_NAME, label: 'Pickup Country', selected: true, type: 'String' }
        ]
      },
      {
        label: 'Weight & Dimensions',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.WEIGHT, label: 'Weight', selected: true, type: 'Number' },
          { name: constants.PARCEL_FIELD_KEYS.VOLUME, label: 'Volume', selected: true, type: 'Number' },
          { name: constants.PARCEL_FIELD_KEYS.LENGTH, label: 'Length', selected: true, type: 'Number' },
          { name: constants.PARCEL_FIELD_KEYS.WIDTH, label: 'Width', selected: true, type: 'Number' },
          { name: constants.PARCEL_FIELD_KEYS.HEIGHT, label: 'Height', selected: true, type: 'Number' }
        ]
      },
      {
        label: 'Delivery Info',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.BOOKED_PICKUP_DATE, type: 'Date', label: 'Pickup Date', selected: true },
          { name: constants.PARCEL_FIELD_KEYS.BOOKED_TIMESTAMP, type: 'Time', label: 'Booked', selected: true },
          { name: constants.PARCEL_FIELD_KEYS.PICKUP_TIMESTAMP, type: 'Time', label: 'Act. Pickup', selected: true },
          { name: constants.PARCEL_FIELD_KEYS.DELIVERY_TIMESTAMP, type: 'Time', label: 'Delivery', selected: true },
          { name: constants.PARCEL_FIELD_KEYS.ENGINEER_PICKUP, type: 'Time', label: 'Eng. Pickup', selected: true },
          { name: constants.PARCEL_FIELD_KEYS.SIGNED_BY_NAME, label: 'Signed By', selected: true, type: 'String' }
        ]
      },
      {
        label: 'References',
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.PARCEL_ID, label: 'Parcel ID', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.SHIPMENT_REFERENCE, label: 'Shipment Ref.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.DELIVERY_NOTE_NUMBER, label: 'Delivery Note', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.ORDER_NUMBER, label: 'Order Nr.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.SERVICE_ORDER, label: 'Service Order', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.BARCODE, label: 'Barcode', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.CMR_NUMBER, label: 'CMR Nr.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.PICKUP_REFERENCE, label: 'Pickup Ref.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.STORAGE_NUMBER, label: 'Storage Nr.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.CUSTOMS_DECLARATION_NUMBER, label: 'Customs Decl. Nr.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.CUSTOMER_RETURN_REFERENCE, label: 'Customer Ret. Ref.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.RETURN_BARCODE, label: 'Return Barcode', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.FORWARDER_RETURN_REFERENCE, label: 'Forwarder Ret. Ref.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.FORWARD_COUNTER_NUMBER, label: 'Forward Counter Nr.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.RETURN_COUNTER_NUMBER, label: 'Ret. Counter Nr.', selected: true, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.RETURN_PURCHASE_ORDER, label: 'Ret. Purchase Order', selected: false, type: 'String' },
          { name: constants.REFERENCE_TYPE_NAME.ACCOUNT_NUMBER, label: 'Account Number', selected: false, type: 'String' }
        ]
      },
      {
        label: constants.ROUTING,
        columns: [
          { name: constants.PARCEL_FIELD_KEYS.TAGS, label: 'Tags', selected: true, type: 'String' },
          { name: constants.PARCEL_FIELD_KEYS.PROVIDERS, label: 'Providers', selected: true, type: 'String' }
        ]
      }
    ]
    return {
      constants: constants,
      parcelList: [],
      parcelListOriginal: [],
      loading_parcels: false,
      viewMode: 'parcelList',
      allColumnsShown: false,
      listHeaderColumnGroupsOrig: listHeaderColumnGroupsOrig,
      listHeaderColumnGroups: null,
      summaryItems: [],
      selectedParcels: [],
      parcelID: null,
      fuse: null,
      parcelsToDelete: [],
      parcelsToReactivate: [],
      parcelsToCreateCMR: [],
      filterTerm: '',
      unsubscribeParcelFilter: null,
      mobileListHeaderColumnGroups: [
        {
          label: 'ID',
          columns: [{ name: constants.REFERENCE_TYPE_NAME.COUNTER_NUMBER, label: 'Counter Number', selected: true, long: true, fixed: true, type: 'String' }]
        },
        {
          label: 'General Info',
          columns: [
          ]
        },
        {
          label: 'Current Status',
          columns: [
            { name: constants.PARCEL_FIELD_KEYS.CURRENT_PARCEL_STATUS, label: 'Parcel Status', selected: true, type: 'String' },
            { name: constants.PARCEL_FIELD_KEYS.PROBLEM_TYPE_DESCRIPTION, label: 'Problem', selected: true, bold: true, type: 'String' }

          ]
        },
        {
          label: 'Pickup Address',
          columns: [
          ]
        },
        {
          label: 'Weight & Dimensions',
          columns: [
          ]
        },
        {
          label: 'Delivery Info',
          columns: [
          ]
        },
        {
          label: 'References',
          columns: [
          ]
        }
      ],
      mobileEngineerSiemensListHeaderColumnGroups: [
        {
          label: 'ID',
          columns: [{ name: constants.REFERENCE_TYPE_NAME.COUNTER_NUMBER, label: 'Counter Number', selected: true, long: true, fixed: true, type: 'String' },
            { name: constants.REFERENCE_TYPE_NAME.SERVICE_ORDER, label: 'Service Order', selected: true, bold: true, type: 'String' }]
        },
        {
          label: 'General Info',
          columns: [
          ]
        },
        {
          label: 'Current Status',
          columns: [
            { name: constants.PARCEL_FIELD_KEYS.CURRENT_PARCEL_STATUS, label: 'Parcel Status', selected: true, type: 'String' }
          ]
        },
        {
          label: 'Pickup Address',
          columns: [
          ]
        },
        {
          label: 'Weight & Dimensions',
          columns: [
          ]
        },
        {
          label: 'Delivery Info',
          columns: [
          ]
        },
        {
          label: 'References',
          columns: [
          ]
        }
      ]
    }
  },
  computed: {
    ...mapGetters('parcelFilter', ['parcelFilter']),
    ...mapGetters('user', ['getCountryPermissions', 'getCustomerPermissions']),
    // Counts all the rows which are in the table
    totalRowCount () {
      // Return blank while loading
      if (this.loading_parcels) {
        return ''
      } else {
        return this.numberFormat(this.parcelList.length)
      }
    },
    showActionBar () {
      return this.userHasActionBarPermissions() &&
            this.selectedParcels.length > 0 &&
            this.viewMode === 'parcelList'
    }
  },

  created () {
    if (this.$route.query.search) {
      this.searchParcels(this.$route.query.search)
    }
    this.unsubscribeParcelFilter = this.$store.subscribe(this.handleParcelFilterMutation)
  },
  beforeDestroy () {
    if (this.unsubscribeParcelFilter) {
      this.unsubscribeParcelFilter()
    }
  },
  mounted () {
    this.load_Column_Setup()
    this.setupFuse()
    if (!this.$route.query.search) {
      this.loadParcelList()
    }
  },
  methods: {
    copyHTML () {
      this.$refs.parcelListTable.copyHTML()
    },

    handleParcelFilterMutation (mutation) {
      if (mutation.type === 'parcelFilter/setParcelFilter') {
        this.loadParcelList()
      }
    },

    handleLoadAnyway () {
      this.viewMode = 'parcelList'
      this.loadParcelList(true)
    },

    getColumnGroup () {
      if (this.$mq === 'sm') {
        if (this.userHasPermission(constants.PERMISSION_TYPES.ENGINEER, constants.PERMISSION_VALUES.SIEMENS)) {
          return this.mobileEngineerSiemensListHeaderColumnGroups
        }
        return this.mobileListHeaderColumnGroups
      }
      return this.listHeaderColumnGroups
    },

    // Load the parcel list based on the filter model.
    loadParcelList (loadAnyway:boolean = false) {
      var filter = this.parcelFilter('parcel')
      if (filter.Countries.length === 0 &&
      filter.Customers.length === 0 &&
      filter.Parcel_Status.length === 0 &&
      filter.Service_Levels.length === 0 &&
      filter.Current_Event_Infos.length === 0 &&
      filter.Show_Forward_Flow &&
      filter.From_Postal_Code === '' &&
      filter.To_Postal_Code === '' &&
      this.getCountryPermissions.length === 0 &&
      this.getCustomerPermissions === 0 &&
      !loadAnyway) {
        this.viewMode = 'guard'
        return
      }
      this.viewMode = 'parcelList'
      this.loading_parcels = true
      webApi.parcel.get_Parcel_List_By_Filter(filter)
        .then((response) => {
          this.prepareParcelList(response.data)
          this.loading_parcels = false
        }).catch((response) => {
          this.loading_parcels = false
          window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
        })
    },

    // The user has clicked search (or the page has been loaded with the search query)
    searchParcels (searchString) {
      if (this.viewMode !== 'parcelList') {
        this.onDetailClosed(false, null, false)
      }
      this.loading_parcels = true

      webApi.parcel.search_Parcel_By_Reference(searchString).then((response) => {
        this.prepareParcelList(response.data)
        this.loading_parcels = false
      }).catch((response) => {
        this.loading_parcels = false
        window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      })
    },

    reloadParcelList () {
      if (this.$route.query.search) {
        this.searchParcels(this.$route.query.search)
      } else {
        this.loadParcelList()
      }
    },

    // For better handling reads all fields from References and puts them flat into the parcel
    // In addition the parcel status is read and added
    prepareParcelList (loadedList) {
      if (!loadedList) {
        this.parcelList = loadedList
      } else {
        const tempList = JSON.parse(JSON.stringify(loadedList))
        for (let i = 0; i < tempList.length; i++) {
          const parcel = tempList[i]
          for (const key in constants.REFERENCE_TYPE_NAME) {
            if (Object.prototype.hasOwnProperty.call(constants.REFERENCE_TYPE_NAME, key)) {
              const reference = constants.REFERENCE_TYPE_NAME[key]
              this.resolveReference(parcel, reference)
            }
          }
          parcel[constants.PARCEL_FIELD_KEYS.PROVIDERS] = map(parcel[constants.PARCEL_FIELD_KEYS.PROVIDERS], 'Provider_Name').join(', ')
          parcel[constants.PARCEL_FIELD_KEYS.TAGS] = map(parcel[constants.PARCEL_FIELD_KEYS.TAGS], 'Tag_Name').join(', ')
          parcel[constants.PARCEL_FIELD_KEYS.CURRENT_PARCEL_STATUS] = this.get_Parcel_Status(parcel)
          parcel[constants.PARCEL_FIELD_KEYS.TRACKER_LINK_PROVIDER] = this.getExternalLink(parcel, constants).providerName
          parcel[constants.PARCEL_FIELD_KEYS.TRACKER_LINK] = this.getExternalLink(parcel, constants).link
        }
        this.parcelList = tempList
        this.parcelListOriginal = tempList.slice()
      }
    },
    // gets the reference from the References Array and puts it flat into the parcel
    resolveReference (parcel, referenceID) {
      const foundReference:any = find(parcel.References, { Reference_Type: referenceID })
      if (foundReference) {
        parcel[referenceID] = foundReference.Reference_Text
      }
    },

    // ***********************get Parcel Status ***********************************
    // Depending on the parcel status we will set the text in the parcel list from the constants
    get_Parcel_Status (parcel) {
      var latestStatus = ''
      if (parcel.Parcel_Status_BOOKED === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_BOOKED
      }
      if (parcel.Parcel_Status_NOT_BOOKED === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_NOT_BOOKED
      }
      if (parcel.Parcel_Status_COLLECTED === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_COLLECTED
      }
      if (parcel.Parcel_Status_NOT_COLLECTED === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_NOT_COLLECTED
      }
      if (parcel.Parcel_Status_BOOKED_AND_COLLECTED === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_BOOKED_AND_COLLECTED
      }
      if (parcel.Parcel_Status_At_Provider === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_AT_PROVIDER
      }
      if (parcel.Parcel_Status_IN_TRANSIT === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_IN_TRANSIT
      }
      if (parcel.Parcel_Status_OUT_FOR_DELIVERY === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_OUT_FOR_DELIVERY
      }
      if (parcel.Parcel_Status_DELIVERED === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_DELIVERED
      }
      if (parcel.Parcel_Status_ON_TIME === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_ON_TIME
      }
      if (parcel.Parcel_Status_EXTERNAL_DELAY === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_EXTERNAL_DELAY
      }
      if (parcel.Parcel_Status_LATE === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_LATE
      }
      if (parcel.Parcel_Status_EARLY === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_EARLY
      }
      if (parcel.Cancelled === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_CANCELLED
      }
      if (parcel.Parcel_Status_HAS_REPORTED_EXCEPTION === true) {
        latestStatus = constants.PARCEL_LIST.PARCEL_STATUS_TEXT_HAS_REPORTED_EXCEPTION
      }
      return latestStatus
    },

    onSelectionChanged (selectedParcels) {
      this.selectedParcels = selectedParcels
      var booked = size(filter(selectedParcels, (parcel) => {
        return parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_BOOKED] === true ||
        parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_NOT_COLLECTED] === true
      }))

      var collected = size(filter(selectedParcels, (parcel) => {
        return parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_COLLECTED] === true ||
        parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_BOOKED_AND_COLLECTED] === true ||
        parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_AT_PROVIDER] === true ||
        parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_IN_TRANSIT] === true ||
        parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_OUT_FOR_DELIVERY] === true ||
        parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_NOT_BOOKED] === true
      }))

      if (this.userHasPermission(constants.PERMISSION_TYPES.DASHBOARD, constants.PERMISSION_VALUES.EARLY_COLUMN)) {
        var early = size(filter(selectedParcels, (parcel) => {
          return parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_EARLY] === true
        }))
      }

      var ontime = size(filter(selectedParcels, (parcel) => {
        return parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_ON_TIME] === true
      }))

      var late = size(filter(selectedParcels, (parcel) => {
        return parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_LATE] === true
      }))

      var extdelay = size(filter(selectedParcels, (parcel) => {
        return parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_EXTERNAL_DELAY] === true
      }))

      //  Initialize the fields
      var volume = 0
      var grWeight = 0
      var company = ''
      var country = ''

      volume = sumBy(selectedParcels, constants.PARCEL_FIELD_KEYS.VOLUME)
      grWeight = sumBy(selectedParcels, constants.PARCEL_FIELD_KEYS.WEIGHT)

      // Check if its the same company or if it is a mixed selection
      selectedParcels.forEach(row => {
        if (company === '') {
          company = row[constants.PARCEL_FIELD_KEYS.CUSTOMER_NAME]
        } else if (company !== row[constants.PARCEL_FIELD_KEYS.CUSTOMER_NAME]) {
          company = 'Mixed'
        }
      })
      // Check if its the same country or if it is a mixed selection
      selectedParcels.forEach(row => {
        if (country === '') {
          country = row[constants.PARCEL_FIELD_KEYS.COUNTRY_NAME]
        } else if (country !== row[constants.PARCEL_FIELD_KEYS.COUNTRY_NAME]) {
          country = 'Mixed'
        }
      })

      this.summaryItems = []
      this.summaryItems.push({ label: 'Company', value: company, key: 'company' })
      this.summaryItems.push({ label: 'Country', value: country, key: 'country' })
      this.summaryItems.push({ label: 'Booked', value: booked, key: 'booked' })
      this.summaryItems.push({ label: 'Collected', value: collected, key: 'collected' })
      if (this.userHasPermission(constants.PERMISSION_TYPES.DASHBOARD, constants.PERMISSION_VALUES.EARLY_COLUMN)) {
        this.summaryItems.push({ label: 'Early', value: early, key: 'early' })
      }
      this.summaryItems.push({ label: 'On Time', value: ontime, key: 'on-time' })
      this.summaryItems.push({ label: 'Late', value: late, key: 'late' })
      this.summaryItems.push({ label: 'Ext. Delay', value: extdelay, key: 'external-delay' })
      this.summaryItems.push({ label: 'Total', value: this.selectedParcels.length, key: 'total' })
      var netPerfVal = this.numberFormat(((ontime + late + extdelay) === 0) ? 100 : ((ontime + extdelay) / (ontime + late + extdelay) * 100), 2, true) + ' %'
      this.summaryItems.push({ label: 'Net Performance (%)', value: netPerfVal, key: 'net-performance' })
      if (this.userHasPermission(constants.PERMISSION_TYPES.DASHBOARD, constants.PERMISSION_VALUES.GROSS_PERFORMANCE_COLUMN)) {
        var grossPerfVal = this.numberFormat(((ontime) === 0) ? 100 : ((ontime) / (this.selectedParcels.length) * 100), 2, true) + ' %'
        this.summaryItems.push({ label: 'Gross Performance (%)', value: grossPerfVal, key: 'gross-performance' })
      }
      this.summaryItems.push({ label: 'Volume', value: this.numberFormat(volume, 3, true) + ' m3', key: 'volume' })
      this.summaryItems.push({ label: 'Gr. Wt.', value: this.numberFormat(grWeight, 2, true) + ' kg', key: 'gross-weight' })
    },

    onParcelDetailOpened (parcelID) {
      this.viewMode = 'parcelDetails'
      this.parcelID = parcelID
      window.history.pushState({}, null, '#/parcel-detail/' + parcelID)
      window.onpopstate = (event) => {
        this.viewMode = 'parcelList'
        window.onpopstate = null
      }
    },

    onDetailClosed (openUpdateAfterClosing, parcelDetail, goBack) {
      this.viewMode = 'parcelList'
      if (goBack) {
        window.history.back()
      }
      if (openUpdateAfterClosing) {
        this.selectedParcels = [find(this.parcelList, { Parcel_ID: parcelDetail.Parcel_ID })]
        this.viewMode = 'update_parcels'
      }
    },

    openUpdateParcelsModal () {
      this.viewMode = 'update_parcels'
    },
    onOpenUploadDocumentModal () {
      window.bus.$emit('openFileUploadModal', map(this.selectedParcels, 'Parcel_ID'))
    },
    onDeactivateEvent () {
      window.bus.$emit('openDeactivateEventModal', map(this.selectedParcels, 'Parcel_ID'))
    },
    /**
     * Function for deactivate the Problem reports for the selected parcels
     */
    onDeactivateProblemReport () {
      webApi.problem_report.deactivate_Problem_Reports_In_Parcel_List(map(this.selectedParcels, 'Parcel_ID'))
        .then((response) => {
          this.$toast('Problem Reports where deactivated', constants.defaultToastOptions())
          this.reloadParcelList()
        }).catch((response) => {
          window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
        })
    },
    onClearAndReloadParcelList () {
      this.selectedParcels = []
      this.$refs.parcelListTable.clearSelection()
      this.reloadParcelList()
    },
    onFileUploaded () {
    },
    onOpenDeleteParcelModal () {
      this.parcelsToDelete = this.selectedParcels
    },
    onOpenReactivateParcelModal () {
      this.parcelsToReactivate = this.selectedParcels
    },

    onOpenCreateCMRModal () {
      this.parcelsToCreateCMR = this.selectedParcels
    },

    onCMRCreated () {
      this.parcelsToCreateCMR = []
      this.reloadParcelList()
    },

    onEventsDeactivated () {
      this.$toast('The events of the selected Parcels where deactivated', constants.defaultToastOptions())
      this.reloadParcelList()
    },
    openCreateAdhocDriveModal () {
      var dateOfOrder = [...new Set(this.selectedParcels.map(a => this.formatDate(a.Booked_Timestamp, false, 'YYYY-MM-DD')))]
      var customerId = [...new Set(this.selectedParcels.map(a => a.Customer_ID))]
      var countryId = [...new Set(this.selectedParcels.map(a => a.Country_ID))]
      var fromCity = [...new Set(this.selectedParcels.map(a => a.Pickup_City))]
      var toCity = [...new Set(this.selectedParcels.map(a => a.Delivery_City))]
      var weight = this.selectedParcels.reduce(function (prev, cur) {
        return prev + cur.Weight
      }, 0)
      var counterNumbers = Object.values(this.selectedParcels.map(a => a['Counter Number'])).join(',')
      var awbNumbers = Object.values(this.selectedParcels.map(a => a.AWB)).join(',')
      var forwarderReference = Object.values(this.selectedParcels.map(a => a['Forwarder Reference'])).join(',')
      var adhocDriveValues = {
        Date_Of_Order: dateOfOrder.length === 1 ? this.formatDate(dateOfOrder[0], false) : this.formatDate(new Date(), false),
        Counter_Number: counterNumbers,
        Country_ID: countryId.length === 1 ? countryId[0] : -1,
        Provider_ID: -1,
        Customer_ID: customerId.length === 1 ? customerId[0] : -1,
        From_City: fromCity.length === 1 ? fromCity[0] : '',
        To_City: toCity.length === 1 ? toCity[0] : '',
        Description: '',
        Number_Of_Parcels: this.selectedParcels.length,
        Weight: weight,
        Cost_Coverage_By: 'Customer',
        Agreed_Cost: '',
        Agreed_Price: '',
        Currency_ID: -1,
        AWB_Reference: awbNumbers.replace(/^,/, '').replace(/,\s*$/, ''),
        Forwarder_Reference: forwarderReference.replace(/^,/, '').replace(/,\s*$/, ''),
        Comments: ''
      }
      this.$router.push({ name: 'adhoc-drive-create', params: adhocDriveValues })
    },
    openCreateLinehaulDriveModal () {
      var dateOfOrder = [...new Set(this.selectedParcels.map(a => this.formatDate(a.Booked_Timestamp, false, 'YYYY-MM-DD')))]
      var nrOfCustomers = groupBy(this.selectedParcels, 'Customer_ID').length
      var weight = this.selectedParcels.reduce(function (prev, cur) {
        return prev + cur.Weight
      }, 0)
      var counterNumbers = Object.values(this.selectedParcels.map(a => a['Counter Number'])).join(',')
      // eslint-disable-next-line
      var linehaulDriveValues = {
        Linehaul_Drive_Detail_Date: dateOfOrder.length === 1 ? this.formatDate(dateOfOrder[0], false) : this.formatDate(new Date(), false),
        Linehaul_Drive_ID: -1,
        Linehaul_Drive_Detail_Customers: nrOfCustomers,
        Linehaul_Drive_Detail_Parcels: this.selectedParcels.length,
        Linehaul_Drive_Detail_Weight: weight,
        Linehaul_Drive_Detail_Load_Meters: '',
        Linehaul_Drive_Detail_T1: '',
        Linehaul_Drive_Detail_Stops: ''
      }
      this.$router.push({ name: 'linehaul-road-create', params: linehaulDriveValues })
    },

    // *********************** Columns ***********************************

    expandList () {
      this.listHeaderColumnGroups.forEach((item, index) => {
        item.columns.forEach((subitem, subindex) => {
          this.listHeaderColumnGroups[index].columns[subindex].selected = true
        })
      })
    },

    contractList () {
      this.load_Column_Setup()
    },

    toggleColumn (isSelected, indexes) {
      this.listHeaderColumnGroups[indexes[0]].columns[indexes[1]].selected = isSelected
      this.save_Column_Setup()
    },

    /// Saves the column setup to the window.localStorage
    save_Column_Setup () {
      window.localStorage.setItem('parcel_list_column_setup', JSON.stringify(this.listHeaderColumnGroups))
      const storedHeaderColumnGroups = JSON.parse(window.localStorage.getItem('parcel_list_column_setup'))
      this.listHeaderColumnGroups[1].columns[0].fixedSecond = storedHeaderColumnGroups[0].columns[0].selected
      this.listHeaderColumnGroups[1].columns[0].fixed = !storedHeaderColumnGroups[0].columns[0].selected
    },
    // *********************** Download Excel ***********************************
    // Calls with all selected lines the  excel download
    downloadExcel () {
      var excelList = {
        Columns: [],
        Rows: []
      }
      var selectedFields:any = filter(flatMap(this.listHeaderColumnGroups, 'columns'), { selected: true } as any)
      // Loop through the columns and add them to the string
      for (let i = 0; i < selectedFields.length; i++) {
        const field:any = selectedFields[i]
        excelList.Columns.push({ ID: field.name, Header: field.label, Type: this.getColumnType(field.type) })
      }

      let parcels = this.parcelList
      if (this.selectedParcels.length > 0) {
        parcels = this.selectedParcels
      }

      for (let i = 0; i < parcels.length; i++) {
        var rowValues = { Value: [] }
        const parcel = parcels[i]
        for (let j = 0; j < selectedFields.length; j++) {
          const field:any = selectedFields[j]
          rowValues.Value.push(parcel[field.name])
        }
        excelList.Rows.push(rowValues)
      }

      // Call the excel download helper
      this.getexcelDownload(excelList, 'SPLIT_Parcel_List.xlsx')
    },
    /**
     * Gets the column type for a certain field (converts from internal type to backend type)
     */
    getColumnType (fieldType) {
      // "Number", "DateTime", "Text", "Boolean"
      if (fieldType) {
        switch (fieldType) {
          case 'Number':
            return 'Number'

          case 'Boolean' :
            return 'Boolean'

          case 'Date':
          case 'Time':
            return 'DateTime'

          case 'String':
          default: {
            return 'Text'
          }
        }
      }
      return ''
    },

    // ********************************* LOAD, SAVE, AND VALIDATE TABLE SETUP **********************************

    // Loads the Columns setup from the localStorage
    load_Column_Setup () {
      const storedHeaderColumnGroups = JSON.parse(window.localStorage.getItem('parcel_list_column_setup'))
      if (!isEmpty(storedHeaderColumnGroups)) {
        this.listHeaderColumnGroups = this.cloneArray(this.listHeaderColumnGroupsOrig)
        const routingColumnGroupColumns = this.listHeaderColumnGroups[findIndex(this.listHeaderColumnGroups, { label: constants.ROUTING })].columns
        if (!this.userHasPermission(constants.PERMISSION_TYPES.TAG, constants.PERMISSION_VALUES.LIST) && !this.userHasPermission(constants.PERMISSION_TYPES.PROVIDER, constants.PERMISSION_VALUES.LIST)) {
          this.listHeaderColumnGroups.splice(findIndex(this.listHeaderColumnGroups, { label: constants.ROUTING }), 1)
        } else if (!this.userHasPermission(constants.PERMISSION_TYPES.TAG, constants.PERMISSION_VALUES.LIST)) {
          routingColumnGroupColumns.splice(findIndex(routingColumnGroupColumns, { name: constants.PARCEL_FIELD_KEYS.TAGS }), 1)
        } else if (!this.userHasPermission(constants.PERMISSION_TYPES.PROVIDER, constants.PERMISSION_VALUES.LIST)) {
          routingColumnGroupColumns.splice(findIndex(routingColumnGroupColumns, { name: constants.PARCEL_FIELD_KEYS.PROVIDERS }), 1)
        }
        for (let i = 0; i < this.listHeaderColumnGroups.length; i++) {
          const columnGroup = this.listHeaderColumnGroups[i]
          for (let j = 0; j < columnGroup.columns.length; j++) {
            const element = columnGroup.columns[j]
            if (storedHeaderColumnGroups[i] && storedHeaderColumnGroups[i].columns && storedHeaderColumnGroups[i].columns[j]) {
              element.selected = storedHeaderColumnGroups[i].columns[j].selected
            }
          }
        }
      } else {
        this.listHeaderColumnGroups = this.cloneArray(this.listHeaderColumnGroupsOrig)
        this.save_Column_Setup()
      }
    },

    // ***********************Find group in saved setup ***********************************
    /// returns true if the Group is found in the saved setup in the correct index
    find_Group_In_Saved_Setup (defaultGroup, groupIndex, savedSetup) {
      try {
        if (savedSetup.Length < groupIndex + 1) return false
        if (savedSetup[groupIndex].label === defaultGroup.label) return true
      } catch (err) {
        console.error(err)
      }
      return false
    },

    // ***********************Find column in saved setup ***********************************
    /// Returns true if the Column is found in the Saved setup in hte correct group and index.
    find_Column_In_Saved_Setup (groupIndex, defaultColumn, columnIndex, savedSetup) {
      try {
        if (savedSetup.Length < groupIndex + 1) return false
        if (savedSetup[groupIndex].columns.Length < columnIndex + 1) return false
        if (savedSetup[groupIndex].columns[columnIndex].label === defaultColumn.label) return true
      } catch (err) {
        console.error(err)
      }
      return false
    },

    /// ***********************************************************************************

    parcelUpdateCancelled () {
      this.viewMode = 'parcelList'
    },
    parcelUpdateSaved () {
      this.viewMode = 'parcelList'
      this.loading_parcels = false
      this.selectedParcels = []
      this.$refs.parcelListTable.clearSelection()
      this.reloadParcelList()
    },
    setupFuse () {
      var options = {
        shouldSort: true,
        threshold: 0.1,
        matchAllTokens: true,
        location: 0,
        distance: 1000,
        maxPatternLength: 250,
        minMatchCharLength: 3,
        keys: map(flatMap(this.listHeaderColumnGroups, 'columns'), 'name')
      }
      this.fuse = new Fuse(this.parcelListOriginal, options)
    },
    onFilterChange (value) {
      if (!value || value.length < 4) {
        this.parcelList = this.parcelListOriginal
      } else {
        this.fuse.setCollection(this.parcelListOriginal)
        this.parcelList = this.fuse.search(value)
      }
    }
  }
})
</script>

<style lang="scss">
  .parcel-list-header {
    &__filter {
      width: auto !important;
      margin-right: 20px !important;
    }
  }
  .parcel-list__guard {
    width: 100%;
    text-align: center;
    p {
      margin: 50px 0;
    }
  }
</style>
